import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowUpRight, Loader2 } from 'lucide-react';
import { AiWorkspaceLayout, WorkspaceSidebarTitle } from '@/components/AiWorkspaceLayout';
import { FormatContentEditor, sourceContentStatus } from '@/components/FormatContentEditor';
import { PromptTextarea } from '@/components/PromptTextarea';
import {
  ReferenceMaterialsUploader,
  type AttachmentCard,
  isAnalyzingAttachments,
  readyAttachments,
} from '@/components/ReferenceMaterialsUploader';
import { SendModal } from '@/components/SendModal';
import { SidebarCollapsible } from '@/components/SidebarCollapsible';
import { UpgradeModal } from '@/components/UpgradeModal';
import { UserMenu } from '@/components/UserMenu';
import { Wordmark } from '@/components/Wordmark';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { cn } from '@/lib/utils';
import { useSession } from '@/hooks/useSession';
import { getDraft } from '@/lib/drafts';
import {
  LAYOUT_TEMPLATE_NONE_ID,
  TEMPLATE_CATEGORIES,
  type TemplateCategory,
  getCategoryLabel,
  getLayoutTemplateById,
  getLayoutTemplateLabel,
  getTemplatesByCategory,
} from '@/lib/email-layout-templates';
import { streamGenerate, type CopyPolicy, type EmailStyle, type GenerateDone, type GenerationMode } from '@/lib/generate';
import { SOURCE_MAX_CHARS } from '@/lib/attachments';
import { getKlaviyoStatus, pushToKlaviyo } from '@/lib/integrations';
import type { SendResult } from '@/lib/sends';

const STYLES: Array<{ value: EmailStyle; label: string; blurb: string }> = [
  { value: 'direct', label: 'Direct', blurb: 'Bright accent on white — modern retail' },
  { value: 'luxury', label: 'Luxury', blurb: 'Editorial gold — premium fashion & beauty' },
  { value: 'playful', label: 'Playful', blurb: 'Soft pastels — DTC lifestyle & beauty' },
  { value: 'minimal', label: 'Minimal', blurb: 'Refined B&W — boutique & editorial' },
  { value: 'bold', label: 'Bold', blurb: 'Statement hero — athletic & streetwear' },
];

const EXAMPLES = [
  'Flash sale for a skincare brand — 30% off sitewide, code GLOW30, ends midnight Sunday',
  'Launch announcement for a SaaS analytics product — 3 new features, try-it CTA',
  'Weekly newsletter digest for a design blog — 1 featured essay + 2 short reads',
];

const CATEGORY_FILTERS: Array<{ id: 'all' | TemplateCategory; label: string }> = [
  { id: 'all', label: 'All' },
  ...TEMPLATE_CATEGORIES.map((c) => ({ id: c.id, label: c.label })),
];

export function NewDraftPage() {
  const navigate = useNavigate();
  const { user } = useSession();
  const [mode, setMode] = useState<GenerationMode>('brief');
  const [prompt, setPrompt] = useState('');
  const [sourceContent, setSourceContent] = useState('');
  const [formatNotes, setFormatNotes] = useState('');
  const [copyPolicy, setCopyPolicy] = useState<CopyPolicy>('preserve');
  const [attachmentCards, setAttachmentCards] = useState<AttachmentCard[]>([]);
  const [style, setStyle] = useState<EmailStyle>('direct');
  const [layoutTemplateId, setLayoutTemplateId] = useState<string>(LAYOUT_TEMPLATE_NONE_ID);
  const [previewLayoutTemplateId, setPreviewLayoutTemplateId] = useState<string | null>(null);
  const [templateCategory, setTemplateCategory] = useState<'all' | TemplateCategory>('all');
  const [language, setLanguage] = useState('en');

  const [isStreaming, setIsStreaming] = useState(false);
  const [subject, setSubject] = useState<string | null>(null);
  const [previewText, setPreviewText] = useState<string | null>(null);
  const [htmlSoFar, setHtmlSoFar] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [over, setOver] = useState(false);
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const [generatedDraft, setGeneratedDraft] = useState<GenerateDone | null>(null);
  const [sendDraftMeta, setSendDraftMeta] = useState<{
    fromEmail: string | null;
    fromName: string | null;
  } | null>(null);
  const [showSendModal, setShowSendModal] = useState(false);
  const [sendToast, setSendToast] = useState<{ kind: 'success' | 'error'; text: string } | null>(
    null,
  );

  const iframeRef = useRef<HTMLIFrameElement>(null);
  const abortRef = useRef<AbortController | null>(null);
  const [klaviyoConnected, setKlaviyoConnected] = useState(false);
  const [klaviyoPushing, setKlaviyoPushing] = useState(false);

  // Check Klaviyo connection status on mount
  useEffect(() => {
    getKlaviyoStatus()
      .then((s) => setKlaviyoConnected(s.connected))
      .catch(() => setKlaviyoConnected(false));
  }, []);

  useEffect(() => {
    if (!iframeRef.current) return;
    const doc = iframeRef.current.contentDocument;
    if (!doc) return;
    doc.open();
    doc.write(htmlSoFar || placeholderHtml(mode, attachmentCards.length > 0));
    doc.close();
  }, [htmlSoFar, mode, attachmentCards.length]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (isStreaming || !canSubmit) return;
    setIsStreaming(true);
    setError(null);
    setOver(false);
    setGeneratedDraft(null);
    setSendDraftMeta(null);
    setSendToast(null);
    setShowSendModal(false);
    setSubject(null);
    setPreviewText(null);
    setHtmlSoFar('');

    const layoutTemplate = getLayoutTemplateById(layoutTemplateId);
    const readyAtt = readyAttachments(attachmentCards);

    const payload =
      mode === 'brief'
        ? {
            mode: 'brief' as const,
            prompt: layoutTemplate
              ? `${prompt.trim()}\n\n[Layout structure]\n${layoutTemplate.layoutPrompt}`
              : prompt.trim(),
            style,
            language,
            starter_html: layoutTemplate?.starterHtml ?? null,
            template_context: layoutTemplate
              ? {
                  template_id: layoutTemplate.id,
                  template_label: layoutTemplate.label,
                  template_category: layoutTemplate.category,
                  audience_type: layoutTemplate.metadata?.audienceType,
                  campaign_goal: layoutTemplate.metadata?.campaignGoal,
                  campaign_type: layoutTemplate.metadata?.campaignType,
                  required_sections: layoutTemplate.metadata?.requiredSections,
                  optional_sections: layoutTemplate.metadata?.optionalSections,
                  default_cta_intent: layoutTemplate.metadata?.defaultCtaIntent,
                  copy_density: layoutTemplate.metadata?.copyDensity,
                  image_strategy: layoutTemplate.metadata?.imageStrategy,
                }
              : null,
            attachments: readyAtt.length ? readyAtt : undefined,
          }
        : {
            mode: 'format' as const,
            source_content: sourceContent.trim(),
            copy_policy: copyPolicy,
            prompt: formatNotes.trim() || undefined,
            style,
            language,
            starter_html: layoutTemplate?.starterHtml ?? null,
            template_context: layoutTemplate
              ? {
                  template_id: layoutTemplate.id,
                  template_label: layoutTemplate.label,
                  template_category: layoutTemplate.category,
                  audience_type: layoutTemplate.metadata?.audienceType,
                  campaign_goal: layoutTemplate.metadata?.campaignGoal,
                  campaign_type: layoutTemplate.metadata?.campaignType,
                  required_sections: layoutTemplate.metadata?.requiredSections,
                  optional_sections: layoutTemplate.metadata?.optionalSections,
                  default_cta_intent: layoutTemplate.metadata?.defaultCtaIntent,
                  copy_density: layoutTemplate.metadata?.copyDensity,
                  image_strategy: layoutTemplate.metadata?.imageStrategy,
                }
              : null,
            attachments: readyAtt.length ? readyAtt : undefined,
          };

    abortRef.current = new AbortController();
    try {
      await streamGenerate(
        payload,
        {
          onMeta: (m) => {
            setSubject(m.subject);
            setPreviewText(m.preview_text);
          },
          onChunk: (c) => {
            setHtmlSoFar((prev) => prev + c);
          },
          onDone: async (d) => {
            setIsStreaming(false);
            setSubject(d.subject);
            setPreviewText(d.preview_text);
            setGeneratedDraft(d);
            try {
              const draft = await getDraft(d.draft_id);
              setSendDraftMeta({
                fromEmail: draft.from_email,
                fromName: draft.from_name,
              });
            } catch {
              setSendDraftMeta(null);
            }
          },
          onError: (msg) => {
            const isQuota = msg.toLowerCase().includes('quota');
            if (isQuota) {
              setOver(true);
              setUpgradeOpen(true);
            }
            setError(msg);
            setIsStreaming(false);
          },
        },
        abortRef.current.signal,
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Generation failed');
      setIsStreaming(false);
    }
  }

  function handleCancel() {
    abortRef.current?.abort();
    abortRef.current = null;
    setIsStreaming(false);
  }

  function handleEdit() {
    if (!generatedDraft) return;
    navigate(`/app/d/${generatedDraft.draft_id}`);
  }

  function handleOpenSend() {
    if (!generatedDraft || !user) return;
    setShowSendModal(true);
  }

  const isAnalyzing = isAnalyzingAttachments(attachmentCards);
  const formatStatus = sourceContentStatus(sourceContent.trim().length);
  const canSubmit =
    !isAnalyzing &&
    (mode === 'brief'
      ? prompt.trim().length >= 8
      : sourceContent.trim().length >= 8 && sourceContent.trim().length <= SOURCE_MAX_CHARS);

  const formatActionLabel =
    copyPolicy === 'preserve'
      ? 'Format as email'
      : copyPolicy === 'polish'
        ? 'Polish & format'
        : 'Rewrite as email';

  const submitLabel =
    mode === 'brief'
      ? 'Generate email →'
      : formatStatus.tone === 'warn'
        ? `Structure & ${formatActionLabel.toLowerCase()} →`
        : `${formatActionLabel} →`;

  const streamingLabel =
    mode === 'format' && sourceContent.trim().length > 8000
      ? 'Structuring long copy…'
      : 'Generating…';

  const selectedLayoutTemplate = getLayoutTemplateById(layoutTemplateId);
  const previewLayoutTemplate = previewLayoutTemplateId
    ? getLayoutTemplateById(previewLayoutTemplateId)
    : undefined;
  const displayedLayoutTemplate = previewLayoutTemplate ?? selectedLayoutTemplate;
  const isPreviewingLayoutTemplate =
    Boolean(previewLayoutTemplate) && previewLayoutTemplate?.id !== selectedLayoutTemplate?.id;

  return (
    <>
      <AiWorkspaceLayout
        fullHeight={false}
        header={
          <header className="bg-card border-border flex h-14 flex-shrink-0 items-center justify-between border-b px-6">
            <div className="flex min-w-0 items-center gap-4">
              <Wordmark
                to="/app"
                iconOnly
                className="text-foreground hover:text-primary -ml-1 transition-colors"
                size="text-xl"
              />
              <Separator orientation="vertical" className="h-5" />
              <Button variant="ghost" size="sm" asChild className="text-muted-foreground hover:text-foreground -ml-2">
                <Link to="/app">
                  <ArrowLeft className="size-4" />
                  Drafts
                </Link>
              </Button>
              <Separator orientation="vertical" className="h-5" />
              <span className="truncate text-sm font-medium">Generate with AI</span>
            </div>
            <div className="flex flex-shrink-0 items-center gap-3">
              {generatedDraft ? (
                <>
                  {sendToast ? (
                    <span
                      className={cn(
                        'text-xs',
                        sendToast.kind === 'success' ? 'text-success' : 'text-destructive',
                      )}
                    >
                      {sendToast.text}
                    </span>
                  ) : null}
                  <Button variant="outline" size="sm" onClick={handleEdit}>
                    Edit
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={klaviyoPushing}
                    onClick={async () => {
                      if (!klaviyoConnected) {
                        navigate('/app/settings?tab=integrations');
                        return;
                      }
                      if (!generatedDraft) return;
                      setKlaviyoPushing(true);
                      try {
                        const res = await pushToKlaviyo(generatedDraft.draft_id, generatedDraft.subject || 'Untitled', {
                          version: generatedDraft.version,
                        });
                        setSendToast({ kind: 'success', text: `Exported to Klaviyo ✓ (${res.action})` });
                        setTimeout(() => setSendToast(null), 5000);
                      } catch (err) {
                        setSendToast({ kind: 'error', text: err instanceof Error ? err.message : 'Klaviyo export failed' });
                        setTimeout(() => setSendToast(null), 5000);
                      } finally {
                        setKlaviyoPushing(false);
                      }
                    }}
                  >
                    {klaviyoPushing ? (
                      <><Loader2 className="size-3 animate-spin mr-1" />Pushing…</>
                    ) : (
                      <><ArrowUpRight className="size-3 mr-1" />Klaviyo</>
                    )}
                  </Button>
                  <Button size="sm" onClick={handleOpenSend} disabled={!user}>
                    Send →
                  </Button>
                  <Separator orientation="vertical" className="h-5" />
                </>
              ) : null}
              <UserMenu />
            </div>
          </header>
        }
        sidebarHeader={<WorkspaceSidebarTitle>✦ AI</WorkspaceSidebarTitle>}
        sidebar={
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-1.5">
              <Label className="text-muted-foreground text-xs uppercase tracking-wide">Creation mode</Label>
              <div className="bg-muted flex rounded-lg p-1">
                {[
                  { value: 'brief' as GenerationMode, label: '✦ Start from idea' },
                  { value: 'format' as GenerationMode, label: '📄 Format content' },
                ].map((tab) => (
                  <button
                    key={tab.value}
                    type="button"
                    disabled={isStreaming}
                    onClick={() => setMode(tab.value)}
                    className={cn(
                      'flex-1 rounded-md px-3 py-2 text-xs font-medium transition-all disabled:opacity-60',
                      mode === tab.value
                        ? 'bg-background text-primary border border-primary/20 shadow-sm'
                        : 'text-muted-foreground hover:text-foreground',
                    )}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
            </div>

            {mode === 'brief' ? (
              <div>
                <Label className="text-muted-foreground text-xs uppercase tracking-wide">
                  Describe your email
                </Label>
                <div className="mt-2">
                  <PromptTextarea
                    value={prompt}
                    onChange={setPrompt}
                    rows={9}
                    disabled={isStreaming}
                    optimizeContext="draft"
                    optimizeMeta={{ style, language }}
                    placeholder="e.g. Welcome email for a meditation app, soft tone, primary CTA: 'Start your first session'"
                  />
                </div>
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {EXAMPLES.map((ex, i) => (
                    <Button
                      key={i}
                      type="button"
                      variant="outline"
                      size="sm"
                      disabled={isStreaming}
                      onClick={() => setPrompt(ex)}
                      className="text-muted-foreground hover:text-foreground hover:border-primary/30 hover:bg-accent/20 hover:shadow-sm transition-all h-auto rounded-full px-2 py-1 text-[11px]"
                    >
                      {ex.slice(0, 40)}…
                    </Button>
                  ))}
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <FormatContentEditor
                  value={sourceContent}
                  onChange={setSourceContent}
                  copyPolicy={copyPolicy}
                  onCopyPolicyChange={setCopyPolicy}
                  disabled={isStreaming}
                  hasAttachments={attachmentCards.some((a) => a.status === 'ready')}
                />
                <div>
                  <Label className="text-muted-foreground text-xs uppercase tracking-wide">
                    Formatting notes (optional)
                  </Label>
                  <textarea
                    value={formatNotes}
                    onChange={(e) => setFormatNotes(e.target.value)}
                    disabled={isStreaming}
                    rows={3}
                    placeholder={
                      sourceContent.trim().length < 8
                        ? 'Paste content above first'
                        : sourceContent.trim().length < 500
                          ? 'e.g. Add a hero image placeholder, use bold CTA'
                          : sourceContent.trim().length > 5000
                            ? 'e.g. Summarize into 3 key sections, highlight dates'
                            : 'e.g. Lead with the discount, keep footer minimal'
                    }
                    className="border-input bg-background ring-offset-background placeholder:text-muted-foreground focus-visible:ring-ring mt-2 flex w-full rounded-md border px-3 py-2 text-sm focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none disabled:opacity-60"
                  />
                </div>
              </div>
            )}

            <SidebarCollapsible
              title="Reference materials"
              badge={attachmentCards.filter((a) => a.status === 'ready').length > 0
                ? `${attachmentCards.filter((a) => a.status === 'ready').length} file${attachmentCards.filter((a) => a.status === 'ready').length === 1 ? '' : 's'}`
                : undefined
              }
              defaultOpen={mode === 'brief'}
            >
              <ReferenceMaterialsUploader
                attachments={attachmentCards}
                onChange={setAttachmentCards}
                disabled={isStreaming}
              />
            </SidebarCollapsible>

            <SidebarCollapsible
              title="Layout"
              badge={getLayoutTemplateLabel(layoutTemplateId)}
            >
              <button
                type="button"
                disabled={isStreaming}
                onClick={() => {
                  setLayoutTemplateId(LAYOUT_TEMPLATE_NONE_ID);
                  setPreviewLayoutTemplateId(null);
                }}
                className={cn(
                  'mb-2 w-full rounded-md border p-2.5 text-left transition disabled:opacity-60',
                  layoutTemplateId === LAYOUT_TEMPLATE_NONE_ID
                    ? 'border-primary bg-accent ring-primary/30 ring-1'
                    : 'border-border hover:border-foreground/20',
                )}
              >
                <div className="text-foreground text-xs font-medium">Custom</div>
                <p className="text-muted-foreground mt-0.5 text-[10px] leading-snug">
                  No layout wireframe — AI follows your prompt only.
                </p>
              </button>

              <div
                className="mb-2 flex flex-wrap gap-1"
                role="tablist"
                aria-label="Filter templates by category"
              >
                {CATEGORY_FILTERS.map((c) => {
                  const active = templateCategory === c.id;
                  return (
                    <button
                      key={c.id}
                      type="button"
                      role="tab"
                      aria-selected={active}
                      disabled={isStreaming}
                      onClick={() => {
                        setTemplateCategory(c.id);
                        setPreviewLayoutTemplateId(null);
                      }}
                      className={cn(
                        'rounded-full border px-2.5 py-0.5 text-[10px] font-medium transition disabled:opacity-60',
                        active
                          ? 'border-foreground bg-foreground text-background'
                          : 'border-border text-muted-foreground hover:border-foreground/30 hover:text-foreground',
                      )}
                    >
                      {c.label}
                    </button>
                  );
                })}
              </div>

              {(() => {
                const filteredTemplates = getTemplatesByCategory(templateCategory);
                if (filteredTemplates.length === 0) {
                  return (
                    <p className="text-muted-foreground py-3 text-center text-[11px]">
                      No templates in this category yet.
                    </p>
                  );
                }
                return (
                  <ScrollArea
                    className="border-border/70 h-[292px] rounded-md border"
                    onMouseLeave={() => setPreviewLayoutTemplateId(null)}
                  >
                    <div
                      className="grid grid-cols-2 gap-2 p-2"
                      onBlur={(event) => {
                        const nextFocus = event.relatedTarget as Node | null;
                        if (!nextFocus || !event.currentTarget.contains(nextFocus)) {
                          setPreviewLayoutTemplateId(null);
                        }
                      }}
                    >
                      {filteredTemplates.map((t) => {
                        const selected = layoutTemplateId === t.id;
                        const previewing = previewLayoutTemplateId === t.id && !selected;
                        return (
                          <button
                            key={t.id}
                            type="button"
                            disabled={isStreaming}
                            onMouseEnter={() => setPreviewLayoutTemplateId(t.id)}
                            onFocus={() => setPreviewLayoutTemplateId(t.id)}
                            onClick={() => {
                              setLayoutTemplateId(t.id);
                              setPreviewLayoutTemplateId(null);
                            }}
                            className={cn(
                              'group h-[104px] rounded-md border p-1.5 text-left transition disabled:opacity-60',
                              selected
                                ? 'border-primary bg-accent ring-primary/30 ring-1'
                                : previewing
                                  ? 'border-primary/50 bg-muted/40 ring-primary/15 ring-1'
                                : 'border-border hover:border-foreground/20 hover:bg-muted/40',
                            )}
                          >
                            <div
                              className="border-border/50 h-[66px] overflow-hidden rounded border bg-white"
                              aria-hidden
                              dangerouslySetInnerHTML={{ __html: t.previewHtml }}
                            />
                            <div className="mt-1.5 min-w-0">
                              <div className="text-foreground truncate text-[11px] font-semibold leading-4">
                                {t.label}
                              </div>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </ScrollArea>
                );
              })()}

              {displayedLayoutTemplate ? (
                <div className="border-border/80 bg-muted/30 mt-3 min-h-[132px] rounded-md border p-3">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="text-foreground text-xs font-semibold">
                        {displayedLayoutTemplate.label}
                      </div>
                      <div className="text-muted-foreground mt-0.5 text-[9px] font-semibold uppercase tracking-wider">
                        {getCategoryLabel(displayedLayoutTemplate.category)}
                      </div>
                    </div>
                    <div className="flex shrink-0 flex-col items-end gap-1">
                      <span
                        className={cn(
                          'rounded-full border px-2 py-0.5 text-[9px] font-medium',
                          isPreviewingLayoutTemplate
                            ? 'border-primary/40 text-primary'
                            : 'border-border text-muted-foreground',
                        )}
                      >
                        {isPreviewingLayoutTemplate ? 'Previewing' : 'Selected'}
                      </span>
                      {displayedLayoutTemplate.metadata?.copyDensity ? (
                        <span className="border-border text-muted-foreground rounded-full border px-2 py-0.5 text-[9px] font-medium capitalize">
                          {displayedLayoutTemplate.metadata.copyDensity}
                        </span>
                      ) : null}
                    </div>
                  </div>
                  <p className="text-muted-foreground mt-2 text-[11px] leading-relaxed">
                    {displayedLayoutTemplate.description}
                  </p>
                  {displayedLayoutTemplate.metadata ? (
                    <div className="border-border/70 mt-2 space-y-1.5 border-t pt-2">
                      <p className="text-muted-foreground text-[10px] leading-snug">
                        <span className="text-foreground font-medium">Goal:</span>{' '}
                        {displayedLayoutTemplate.metadata.campaignGoal}
                      </p>
                      <p className="text-muted-foreground text-[10px] leading-snug">
                        <span className="text-foreground font-medium">CTA:</span>{' '}
                        {displayedLayoutTemplate.metadata.defaultCtaIntent.replace(/_/g, ' ')}
                      </p>
                    </div>
                  ) : null}
                </div>
              ) : null}
            </SidebarCollapsible>

            {/* ── Brand status ── */}
            {user && !user.hasBrandProfile ? (
              <div className="rounded-lg border border-dashed border-primary/30 bg-accent/30 p-3">
                <div className="flex items-center gap-2">
                  <span className="text-primary text-sm">🎨</span>
                  <span className="text-xs font-medium">No brand set up</span>
                </div>
                <p className="text-muted-foreground mt-1 text-[11px] leading-relaxed">
                  AI will use generic style. Set up your brand for emails that match your voice.
                </p>
                <Link
                  to="/app/brand"
                  className="text-primary hover:underline mt-1.5 inline-block text-[11px] font-medium"
                >
                  Set up brand →
                </Link>
              </div>
            ) : user?.hasBrandProfile ? (
              <div className="rounded-lg border border-primary/15 bg-accent/30 p-3 shadow-sm">
                <div className="flex items-center gap-2">
                  <span className="text-primary text-sm">🎨</span>
                  <span className="text-xs font-medium">Brand active</span>
                </div>
                <p className="text-muted-foreground mt-0.5 text-[11px]">
                  Your brand voice will be applied to this generation.
                </p>
                <Link
                  to="/app/brand"
                  className="text-muted-foreground hover:text-foreground hover:underline mt-1 inline-block text-[11px]"
                >
                  Edit brand →
                </Link>
              </div>
            ) : null}

            <SidebarCollapsible title="Style" badge={STYLES.find((s) => s.value === style)?.label}>
              <div className="grid grid-cols-2 gap-2">
                {STYLES.map((s) => (
                  <button
                    key={s.value}
                    type="button"
                    disabled={isStreaming}
                    onClick={() => setStyle(s.value)}
                    className={cn(
                      'rounded-md border p-2.5 text-left text-sm transition disabled:opacity-60',
                      style === s.value
                        ? 'border-primary bg-accent'
                        : 'border-border hover:border-foreground/20',
                    )}
                  >
                    <div className="text-xs font-medium">{s.label}</div>
                    <div className="text-muted-foreground mt-0.5 text-[10px] leading-snug">
                      {s.blurb}
                    </div>
                  </button>
                ))}
              </div>
            </SidebarCollapsible>

            <div>
              <Label className="text-muted-foreground text-xs uppercase tracking-wide">
                Language
              </Label>
              <Select value={language} onValueChange={setLanguage} disabled={isStreaming}>
                <SelectTrigger className="mt-2 w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="en">English</SelectItem>
                  <SelectItem value="zh">中文</SelectItem>
                  <SelectItem value="ja">日本語</SelectItem>
                  <SelectItem value="es">Español</SelectItem>
                  <SelectItem value="fr">Français</SelectItem>
                  <SelectItem value="de">Deutsch</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {error ? (
              <Alert variant="destructive">
                <AlertDescription>
                  <p>{error}</p>
                  {over ? (
                    <>
                      <p className="text-muted-foreground mt-1 text-xs">
                        Free tier is 20 AI generations/month. Resets on the 1st.
                      </p>
                      <Button
                        type="button"
                        variant="link"
                        className="text-primary mt-2 h-auto p-0 text-xs"
                        onClick={() => setUpgradeOpen(true)}
                      >
                        Upgrade to Pro →
                      </Button>
                    </>
                  ) : null}
                </AlertDescription>
              </Alert>
            ) : null}

            <div className="flex items-center gap-3">
              {!isStreaming ? (
                <Button type="submit" className="flex-1" disabled={!canSubmit}>
                  {isAnalyzing ? 'Analyzing materials…' : submitLabel}
                </Button>
              ) : (
                <>
                  <Badge
                    variant="secondary"
                    className="bg-muted text-muted-foreground flex-1 justify-center gap-2 px-4 py-2.5 text-sm font-normal"
                  >
                    <Loader2 className="size-3 animate-spin" />
                    {streamingLabel}
                  </Badge>
                  <Button type="button" variant="outline" onClick={handleCancel}>
                    Stop
                  </Button>
                </>
              )}
            </div>

            <p className="text-muted-foreground text-[11px]">
              Free tier: 20 generations/month. Uses <strong>Claude Sonnet 4.5</strong> via OpenRouter.
            </p>
          </form>
        }
        main={
          <section className="bg-muted flex min-h-0 min-w-0 flex-1 flex-col">
            <div className="bg-card border-border flex flex-shrink-0 items-center gap-3 border-b px-6 py-3 text-sm">
              <span className="text-muted-foreground">Subject:</span>
              <span className="truncate font-medium">{subject ?? '—'}</span>
              {previewText ? (
                <span className="text-muted-foreground/70 truncate">· {previewText}</span>
              ) : null}
            </div>
            <div className="min-h-0 flex-1 overflow-auto p-6">
              <div className="border-border mx-auto max-w-[640px] overflow-hidden rounded-md border bg-white shadow-sm">
                <iframe
                  ref={iframeRef}
                  title="Live preview"
                  className="w-full"
                  style={{ height: '70vh', border: 0 }}
                />
              </div>
            </div>
          </section>
        }
      />

      {showSendModal && generatedDraft && user ? (
        <SendModal
          draft={{
            id: generatedDraft.draft_id,
            currentVersion: generatedDraft.version,
            subject: generatedDraft.subject,
            fromEmail: sendDraftMeta?.fromEmail ?? null,
            fromName: sendDraftMeta?.fromName ?? null,
          }}
          userEmail={user.email}
          onBeforeSend={async () => generatedDraft.version}
          onClose={() => setShowSendModal(false)}
          onSent={(result: SendResult) => {
            setShowSendModal(false);
            if (result.status === 'sent') {
              setSendToast({
                kind: 'success',
                text: 'Sent ✓ — view in Sent history',
              });
            } else {
              setSendToast({
                kind: 'error',
                text: `Send failed: ${result.error ?? 'Unknown error'}`,
              });
            }
            setTimeout(() => setSendToast(null), 5000);
          }}
        />
      ) : null}

      <UpgradeModal
        open={upgradeOpen}
        currentTier={user?.tier ?? 'free'}
        context="You've hit the Free tier's 20 generations/month. Pro lifts that to 500 — upgrade in two clicks."
        onClose={() => setUpgradeOpen(false)}
      />
    </>
  );
}

function placeholderHtml(mode: GenerationMode, hasAttachments: boolean): string {
  const title =
    mode === 'format'
      ? 'We’ll preserve your copy and turn it into email-safe HTML'
      : 'Your generated email will appear here';
  const body =
    mode === 'format'
      ? 'Paste your content on the right, choose layout and style, then format into a complete HTML email.'
      : 'Describe what you want in the panel on the right, then hit Generate — Claude will write a complete HTML email and we’ll save it as a new draft.';
  const extra = hasAttachments
    ? '<p style="font-size:13px;line-height:1.6;max-width:380px;margin:12px auto 0;color:#5a5d66;">Reference materials will be summarized before generation.</p>'
    : '';
  return `<!doctype html><html><body style="font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;background:#f3f2ec;margin:0;padding:80px 24px;color:#5a5d66;text-align:center;">
<p style="font-size:14px;letter-spacing:0.1em;text-transform:uppercase;color:#9aa0aa;margin:0 0 12px;">Live preview</p>
<h1 style="font-size:24px;color:#0b0c0f;margin:0 0 8px;font-weight:600;">${title}</h1>
<p style="font-size:14px;line-height:1.6;max-width:380px;margin:0 auto;">${body}</p>
${extra}
</body></html>`;
}
